import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import api, { formatApiErrorDetail } from "../lib/api";
import { useAuth } from "../lib/auth";
import MapView from "../components/MapView";
import { StarRating } from "../components/StarRating";
import CategoryIcon from "../components/CategoryIcon";
import { Button } from "../components/ui/button";
import { Textarea } from "../components/ui/textarea";
import { Card } from "../components/ui/card";
import { toast } from "sonner";
import { ArrowLeft, MapPin, Phone, Clock, Star, BedDouble, Car, Home as HomeIcon, Wrench, ImageOff } from "lucide-react";
import { CATEGORIES } from "../lib/categories";

function SpecRow({ icon: Icon, label, value }) {
  if (!value) return null;
  return (
    <div className="flex items-start gap-3">
      <Icon className="w-4 h-4 text-brand-terracotta mt-0.5 shrink-0" />
      <div>
        <div className="text-xs uppercase tracking-wider text-brand-inkMuted font-medium">{label}</div>
        <div className="text-sm text-brand-ink font-medium">{value}</div>
      </div>
    </div>
  );
}

export default function PlaceDetailPage() {
  const { id } = useParams();
  const { user } = useAuth();
  const [place, setPlace] = useState(null);
  const [reviews, setReviews] = useState([]);
  const [loading, setLoading] = useState(true);
  const [rating, setRating] = useState(5);
  const [comment, setComment] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const loadAll = async () => {
    setLoading(true);
    try {
      const [r, rev] = await Promise.all([
        api.get(`/places/${id}`),
        api.get(`/places/${id}/reviews`),
      ]);
      setPlace(r.data);
      setReviews(rev.data);
    } catch {
      setPlace(null);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadAll();
    // eslint-disable-next-line
  }, [id]);

  const submitReview = async (e) => {
    e.preventDefault();
    if (!comment.trim()) {
      toast.error("Veuillez écrire un commentaire");
      return;
    }
    setSubmitting(true);
    try {
      await api.post(`/places/${id}/reviews`, { rating, comment });
      toast.success("Avis publié");
      setComment("");
      setRating(5);
      await loadAll();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Erreur");
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="max-w-6xl mx-auto px-5 sm:px-8 py-12">
        <div className="h-80 bg-brand-sandlight rounded-2xl animate-pulse mb-6" />
        <div className="h-8 bg-brand-sandlight rounded w-1/2 animate-pulse" />
      </div>
    );
  }

  if (!place) {
    return (
      <div className="max-w-3xl mx-auto px-5 sm:px-8 py-20 text-center">
        <h2 className="font-heading text-2xl font-semibold mb-3">Annonce introuvable</h2>
        <Link to="/" className="text-brand-terracotta font-medium hover:underline">
          ← Retour à l'accueil
        </Link>
      </div>
    );
  }

  const cat = CATEGORIES[place.category] || CATEGORIES.restaurant;

  return (
    <div className="max-w-6xl mx-auto px-5 sm:px-8 py-8" data-testid="place-detail">
      <Link
        to={`/${cat.slug}`}
        className="inline-flex items-center gap-2 text-sm font-medium text-brand-inkSoft hover:text-brand-ink mb-6"
      >
        <ArrowLeft className="w-4 h-4" />
        Retour à {cat.label.toLowerCase()}
      </Link>

      <div className="grid grid-cols-1 lg:grid-cols-[1.2fr_1fr] gap-6 mb-10">
        <div
          className="rounded-2xl overflow-hidden border border-brand-line aspect-[16/10]"
          style={{ backgroundColor: place.image_url ? "transparent" : `${cat.color}12` }}
          data-testid={place.image_url ? "place-detail-image" : "place-detail-placeholder"}
        >
          {place.image_url ? (
            <img
              src={place.image_url}
              alt={place.name}
              className="w-full h-full object-cover"
              onError={(e) => { e.currentTarget.style.display = "none"; }}
            />
          ) : (
            <div className="w-full h-full flex flex-col items-center justify-center gap-3 text-center px-6">
              <div
                className="w-16 h-16 rounded-3xl flex items-center justify-center text-white shadow-md"
                style={{ backgroundColor: cat.color }}
                aria-hidden="true"
              >
                <ImageOff className="w-7 h-7" strokeWidth={1.6} />
              </div>
              <p className="text-xs uppercase tracking-[0.18em] font-bold text-brand-inkMuted">
                Photo bientôt disponible
              </p>
              <p className="text-xs text-brand-inkMuted max-w-xs">
                L'établissement n'a pas encore partagé de photo officielle.
              </p>
            </div>
          )}
        </div>
        <div className="flex flex-col">
          <div
            className="inline-flex items-center gap-2 self-start px-3 py-1 rounded-full text-xs uppercase tracking-[0.18em] font-bold text-white mb-3"
            style={{ backgroundColor: cat.color }}
          >
            <CategoryIcon name={cat.icon} className="w-3.5 h-3.5" />
            {cat.singular}
          </div>
          <h1 className="font-heading text-3xl sm:text-4xl font-semibold text-brand-ink leading-tight tracking-tight mb-4" data-testid="place-name">
            {place.name}
          </h1>
          <div className="flex items-center gap-3 mb-5 flex-wrap">
            <div className="flex items-center gap-1.5">
              <Star className="w-5 h-5 fill-brand-terracotta text-brand-terracotta" />
              <span className="font-semibold text-brand-ink text-lg">
                {place.avg_rating?.toFixed(1) || "—"}
              </span>
            </div>
            <span className="text-sm text-brand-inkMuted">{place.review_count} avis</span>
            <span className="text-sm text-brand-inkMuted">·</span>
            <span className="text-sm font-medium text-brand-ink">{place.price_range}</span>
            {place.subtype && (
              <>
                <span className="text-sm text-brand-inkMuted">·</span>
                <span className="text-sm font-medium text-brand-ink">{place.subtype}</span>
              </>
            )}
          </div>
          {place.description && (
            <p className="text-brand-inkSoft leading-relaxed mb-6">{place.description}</p>
          )}

          {/* Category-specific specs */}
          <div className="grid grid-cols-2 gap-x-6 gap-y-4 mb-6">
            <SpecRow icon={MapPin} label="Adresse" value={`${place.address}, ${place.city}`} />
            {place.phone && <SpecRow icon={Phone} label="Téléphone" value={place.phone} />}
            {place.opening_hours && (
              <SpecRow icon={Clock} label="Horaires" value={place.opening_hours} />
            )}
            {place.category === "hotel" && place.stars && (
              <SpecRow icon={BedDouble} label="Classement" value={`${place.stars} étoiles`} />
            )}
            {place.category === "rental" && (
              <>
                {place.monthly_rent && (
                  <SpecRow icon={HomeIcon} label="Loyer mensuel" value={place.monthly_rent} />
                )}
                {place.bedrooms && (
                  <SpecRow icon={HomeIcon} label="Chambres" value={`${place.bedrooms} chambre${place.bedrooms > 1 ? "s" : ""}`} />
                )}
              </>
            )}
            {place.category === "car_sale" && (
              <>
                {place.brand && <SpecRow icon={Car} label="Marque" value={place.brand} />}
                {place.model && <SpecRow icon={Car} label="Modèle" value={place.model} />}
                {place.year && <SpecRow icon={Car} label="Année" value={place.year} />}
                {place.mileage_km && (
                  <SpecRow icon={Car} label="Kilométrage" value={`${place.mileage_km.toLocaleString("fr-FR")} km`} />
                )}
                {place.transmission && (
                  <SpecRow icon={Car} label="Transmission" value={place.transmission} />
                )}
                {place.sale_price && <SpecRow icon={Car} label="Prix de vente" value={place.sale_price} />}
              </>
            )}
            {place.category === "car_rental" && (
              <>
                {place.brand && <SpecRow icon={Car} label="Marque" value={place.brand} />}
                {place.model && <SpecRow icon={Car} label="Modèle" value={place.model} />}
                {place.transmission && (
                  <SpecRow icon={Car} label="Transmission" value={place.transmission} />
                )}
                {place.daily_rate && (
                  <SpecRow icon={Car} label="Tarif journalier" value={place.daily_rate} />
                )}
              </>
            )}
            {place.category === "mechanic" && place.subtype && (
              <SpecRow icon={Wrench} label="Spécialités" value={place.subtype} />
            )}
          </div>
        </div>
      </div>

      {/* Map */}
      <div className="h-80 mb-12">
        <MapView places={[place]} center={[place.lat, place.lng]} zoom={14} autoFit={false} />
      </div>

      {/* Reviews */}
      <section>
        <h2 className="font-heading text-2xl sm:text-3xl font-semibold text-brand-ink tracking-tight mb-6">
          Avis ({reviews.length})
        </h2>

        {user && user !== false ? (
          <Card className="rounded-2xl border-brand-line p-6 mb-8 bg-white">
            <form onSubmit={submitReview} className="space-y-4" data-testid="review-form">
              <div>
                <label className="text-sm font-medium text-brand-ink mb-2 block">Votre note</label>
                <StarRating value={rating} onChange={setRating} size={28} testId="rating-input" />
              </div>
              <div>
                <label className="text-sm font-medium text-brand-ink mb-2 block">Votre avis</label>
                <Textarea
                  value={comment}
                  onChange={(e) => setComment(e.target.value)}
                  placeholder="Partagez votre expérience..."
                  className="min-h-[100px] rounded-xl border-brand-line"
                  data-testid="review-comment-input"
                />
              </div>
              <Button
                type="submit"
                disabled={submitting}
                className="rounded-full bg-brand-terracotta hover:bg-brand-terracottaHover text-white"
                data-testid="submit-review-button"
              >
                {submitting ? "Publication..." : "Publier l'avis"}
              </Button>
            </form>
          </Card>
        ) : (
          <Card className="rounded-2xl border-brand-line p-6 mb-8 bg-brand-sandlight text-center">
            <p className="text-brand-inkSoft mb-3">Connectez-vous pour laisser un avis.</p>
            <Link to="/login">
              <Button className="rounded-full bg-brand-terracotta hover:bg-brand-terracottaHover text-white">
                Se connecter
              </Button>
            </Link>
          </Card>
        )}

        <div className="space-y-4" data-testid="reviews-list">
          {reviews.length === 0 ? (
            <p className="text-brand-inkMuted text-center py-8">
              Aucun avis pour le moment. Soyez le premier !
            </p>
          ) : (
            reviews.map((r) => (
              <Card key={r.id} className="rounded-2xl border-brand-line p-5 bg-white">
                <div className="flex items-start justify-between mb-2 gap-3">
                  <div>
                    <div className="font-semibold text-brand-ink">{r.user_name}</div>
                    <div className="text-xs text-brand-inkMuted">
                      {new Date(r.created_at).toLocaleDateString("fr-FR", {
                        day: "numeric",
                        month: "long",
                        year: "numeric",
                      })}
                    </div>
                  </div>
                  <StarRating value={r.rating} readOnly size={16} />
                </div>
                <p className="text-brand-inkSoft leading-relaxed">{r.comment}</p>
              </Card>
            ))
          )}
        </div>
      </section>
    </div>
  );
}
